import React, { useState, useEffect, useRef, useCallback } from 'react';

// ─── Keyframe styles injected once ───────────────────────────────────────────
const CAROUSEL_STYLES = `
  @keyframes kb-zoom-right {
    0%   { transform: scale(1.04) translateX(0); }
    100% { transform: scale(1.13) translateX(-2.5%); }
  }
  @keyframes kb-zoom-left {
    0%   { transform: scale(1.04) translateX(0); }
    100% { transform: scale(1.13) translateX(2.5%); }
  }
  @keyframes kb-zoom-up {
    0%   { transform: scale(1.04) translateY(0); }
    100% { transform: scale(1.12) translateY(-2%); }
  }
  @keyframes kb-zoom-down {
    0%   { transform: scale(1.04) translateY(0); }
    100% { transform: scale(1.11) translateY(1.5%); }
  }
  @keyframes kb-gentle {
    0%   { transform: scale(1.04); }
    100% { transform: scale(1.10); }
  }
  @keyframes hero-light-leak {
    0%   { opacity: 0; left: -35%; }
    20%  { opacity: 1; }
    80%  { opacity: 0.7; }
    100% { opacity: 0; left: 125%; }
  }
  @keyframes hero-text-rise {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes hero-progress {
    from { width: 0%; }
    to   { width: 100%; }
  }
  .htx-0 { animation: hero-text-rise 0.65s 0.0s cubic-bezier(0.4,0,0.2,1) both; }
  .htx-1 { animation: hero-text-rise 0.65s 0.12s cubic-bezier(0.4,0,0.2,1) both; }
  .htx-2 { animation: hero-text-rise 0.65s 0.24s cubic-bezier(0.4,0,0.2,1) both; }
  .htx-3 { animation: hero-text-rise 0.65s 0.37s cubic-bezier(0.4,0,0.2,1) both; }
`;

// ─── Types ────────────────────────────────────────────────────────────────────
interface Slide {
  label: string;
  title: string;
  subtitle: string;
  cta: string;
  imageSrc: string;
  blurSrc: string;
  /** TODO: drop a short video clip in here later (no paid service needed, just add the file).
   *  When present, render a muted autoplay <video> behind the image layer. */
  videoSrc?: string;
  kenBurns: 'kb-zoom-right' | 'kb-zoom-left' | 'kb-zoom-up' | 'kb-zoom-down' | 'kb-gentle';
  ctaTarget: 'apply' | 'login' | 'about' | 'features';
}

// ─── Slide data ───────────────────────────────────────────────────────────────
const SLIDES: Slide[] = [
  {
    label: 'K-12 EDUCATION',
    title: 'K-12 Schools',
    subtitle: 'Cambridge-aligned learning from Pre-KG to Grade 12 — personalised, aspirational, and powered by MEF EduSuite.',
    cta: 'Explore Schools',
    imageSrc: '/hero/scene-1.jpg',
    blurSrc: '/hero/scene-1-blur.jpg',
    videoSrc: '/hero/scene-1.mp4',
    kenBurns: 'kb-zoom-right',
    ctaTarget: 'apply',
  },
  {
    label: 'SKILLS & TRAINING',
    title: 'Short Courses & Training',
    subtitle: 'Professional skills, vocational programmes, and industry certifications for adult learners across Nigeria.',
    cta: 'View Programs',
    imageSrc: '/hero/scene-2.jpg',
    blurSrc: '/hero/scene-2-blur.jpg',
    videoSrc: '/hero/scene-2.mp4',
    kenBurns: 'kb-zoom-left',
    ctaTarget: 'about',
  },
  {
    label: 'HIGHER EDUCATION',
    title: 'Universities',
    subtitle: 'Higher education partnerships and degree programmes — fully managed on the MEF EduSuite platform.',
    cta: 'Learn More',
    imageSrc: '/hero/scene-3.jpg',
    blurSrc: '/hero/scene-3-blur.jpg',
    videoSrc: '/hero/scene-3.mp4',
    kenBurns: 'kb-zoom-up',
    ctaTarget: 'about',
  },
  {
    label: 'DIGITAL LEARNING',
    title: 'Learn on Any Device',
    subtitle: 'AI-powered lessons, CBT assessments, and live virtual classes — accessible anywhere, anytime, on any screen.',
    cta: 'Access My Portal',
    imageSrc: '/hero/scene-4.jpg',
    blurSrc: '/hero/scene-4-blur.jpg',
    videoSrc: '/hero/scene-4.mp4',
    kenBurns: 'kb-zoom-down',
    ctaTarget: 'login',
  },
  {
    label: 'SMART CLASSROOMS',
    title: 'Smart Interactive Classrooms',
    subtitle: 'Live virtual lessons with real-time AI collaboration, interactive smartboard presentations, and instant quizzes.',
    cta: 'See It Live',
    imageSrc: '/hero/scene-5.jpg',
    blurSrc: '/hero/scene-5-blur.jpg',
    videoSrc: '/hero/scene-5.mp4',
    kenBurns: 'kb-gentle',
    ctaTarget: 'login',
  },
  {
    label: 'TECHNOLOGY & DIGITAL · TANG MOU',
    title: 'Cloud Platform & VR Training',
    subtitle: 'Nigerian youth mastering Cloud Platform, Virtual Reality, and E-Commerce — powered by the MEF × TANG China Global Skills Summer Program 2026.',
    cta: 'Explore TANG Programs',
    imageSrc: '/hero/scene-6.jpg',
    blurSrc: '/hero/scene-6-blur.jpg',
    videoSrc: '/hero/scene-6.mp4',
    kenBurns: 'kb-zoom-left',
    ctaTarget: 'about',
  },
  {
    label: 'ENGINEERING & ENERGY · TANG MOU',
    title: 'Solar & Renewable Energy Training',
    subtitle: 'Hands-on Solar PV Plant and Smart Electrical Systems training in Nigeria — with Chinese engineering mentors under the MEF × TANG partnership.',
    cta: 'Explore TANG Programs',
    imageSrc: '/hero/scene-7.jpg',
    blurSrc: '/hero/scene-7-blur.jpg',
    videoSrc: '/hero/scene-7.mp4',
    kenBurns: 'kb-zoom-up',
    ctaTarget: 'about',
  },
  {
    label: 'INDUSTRY & LOGISTICS · TANG MOU',
    title: 'Port Equipment & Smart Supply Chain',
    subtitle: 'Empowering Nigerian youth with Port Equipment, Smart Supply Chain, and New Energy Vehicle skills — connecting talent to global industry opportunities.',
    cta: 'Explore TANG Programs',
    imageSrc: '/hero/scene-8.jpg',
    blurSrc: '/hero/scene-8-blur.jpg',
    videoSrc: '/hero/scene-8.mp4',
    kenBurns: 'kb-zoom-right',
    ctaTarget: 'about',
  },
  {
    label: 'CHINESE LANGUAGE · TANG MOU',
    title: 'Learn Chinese with TANG',
    subtitle: 'Nigerian learners studying Mandarin Chinese — listening, reading, writing and speaking — with TANG\'s certified Chinese language teachers. Open doors to China and the global economy.',
    cta: 'Enrol Now',
    imageSrc: '/hero/scene-9.jpg',
    blurSrc: '/hero/scene-9-blur.jpg',
    videoSrc: '/hero/scene-9.mp4',
    kenBurns: 'kb-zoom-down',
    ctaTarget: 'apply',
  },
  {
    label: 'SMART FARMING',
    title: 'Greenhouse & Smart Agriculture',
    subtitle: 'African girls and young women mastering hydroponic smart farming, crop sensors, and agri-tech — building food security and sustainable livelihoods for the next generation.',
    cta: 'Explore Programs',
    imageSrc: '/hero/scene-10.jpg',
    blurSrc: '/hero/scene-10-blur.jpg',
    videoSrc: '/hero/scene-10.mp4',
    kenBurns: 'kb-zoom-right',
    ctaTarget: 'about',
  },
  {
    label: 'BLOCKCHAIN & CRYPTO',
    title: 'Blockchain & Cryptocurrency Trading',
    subtitle: 'African youth, women, and teenagers learning blockchain technology, cryptocurrency trading, and digital finance — unlocking global financial opportunities from Nigeria.',
    cta: 'Explore Programs',
    imageSrc: '/hero/scene-11.jpg',
    blurSrc: '/hero/scene-11-blur.jpg',
    videoSrc: '/hero/scene-11.mp4',
    kenBurns: 'kb-zoom-left',
    ctaTarget: 'about',
  },
];

const TRANSITION_MS = 1100;
const ADVANCE_MS = 7000;

function getDiff(index: number, current: number, n: number): number {
  let d = index - current;
  if (d > n / 2) d -= n;
  if (d < -n / 2) d += n;
  return d;
}

// ─── Component ────────────────────────────────────────────────────────────────
export const CinematicHeroCarousel: React.FC<{
  onLoginClick: () => void;
  onApplyClick: () => void;
}> = ({ onLoginClick, onApplyClick }) => {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>(SLIDES.map(() => false));
  const [textKey, setTextKey] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const touchStart = useRef<number | null>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>(SLIDES.map(() => null));

  const prefersReduced =
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;

  const isMobile =
    typeof window !== 'undefined' ? window.innerWidth < 768 : false;

  const goTo = useCallback(
    (idx: number) => {
      if (isTransitioning || idx === current) return;
      setIsTransitioning(true);
      setCurrent(idx);
      setTextKey((k: number) => k + 1);
      setTimeout(() => setIsTransitioning(false), TRANSITION_MS);
    },
    [isTransitioning, current]
  );

  const prev = useCallback(
    () => goTo((current - 1 + SLIDES.length) % SLIDES.length),
    [current, goTo]
  );
  const next = useCallback(
    () => goTo((current + 1) % SLIDES.length),
    [current, goTo]
  );

  // Auto-advance
  useEffect(() => {
    if (isPaused) return;
    const id = setInterval(next, ADVANCE_MS);
    return () => clearInterval(id);
  }, [isPaused, next]);

  // Preload next image
  useEffect(() => {
    const nextIdx = (current + 1) % SLIDES.length;
    const img = new Image();
    img.src = SLIDES[nextIdx].imageSrc;
  }, [current]);

  // Play active slide video, pause all others
  useEffect(() => {
    videoRefs.current.forEach((vid: HTMLVideoElement | null, i: number) => {
      if (!vid) return;
      if (i === current) {
        vid.currentTime = 0;
        vid.play().catch(() => {/* autoplay blocked — silent */});
      } else {
        vid.pause();
        vid.currentTime = 0;
      }
    });
  }, [current]);

  // Mouse parallax
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (prefersReduced || isMobile) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const cx = (e.clientX - rect.left) / rect.width - 0.5;
      const cy = (e.clientY - rect.top) / rect.height - 0.5;
      setParallax({ x: cx * 14, y: cy * 9 });
    },
    [prefersReduced, isMobile]
  );

  const handleMouseLeave = () => {
    setIsPaused(false);
    setParallax({ x: 0, y: 0 });
  };

  // Touch swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 48) dx < 0 ? next() : prev();
    touchStart.current = null;
  };

  const handleCTA = (target: Slide['ctaTarget']) => {
    if (target === 'login') return onLoginClick();
    if (target === 'apply') return onApplyClick();
    document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <style>{CAROUSEL_STYLES}</style>

      <section
        id="home"
        ref={containerRef}
        className="relative w-full overflow-hidden"
        style={{ height: '100svh', minHeight: 560 }}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        aria-label="MEF EduSuite program showcase"
      >
        {/* ── 3D Stage ── */}
        <div
          className="absolute inset-0"
          style={{
            perspective: isMobile ? 'none' : '1400px',
            perspectiveOrigin: '50% 50%',
          }}
        >
          {SLIDES.map((slide, index) => {
            const diff = getDiff(index, current, SLIDES.length);
            const isActive = diff === 0;
            const isAdj = Math.abs(diff) === 1;

            let transform = 'translateX(200%) scale(0.7)';
            let opacity = 0;
            let zIndex = 1;

            if (prefersReduced || isMobile) {
              transform = 'none';
              opacity = isActive ? 1 : 0;
              zIndex = isActive ? 10 : 1;
            } else {
              const sign = diff >= 0 ? 1 : -1;
              if (isActive) {
                transform = 'translateX(0) rotateY(0deg) scale(1) translateZ(0)';
                opacity = 1;
                zIndex = 10;
              } else if (isAdj) {
                transform = `translateX(${sign * 72}%) rotateY(${-sign * 28}deg) scale(0.87) translateZ(-60px)`;
                opacity = 0.42;
                zIndex = 5;
              } else {
                transform = `translateX(${sign * 145}%) scale(0.72) translateZ(-180px)`;
                opacity = 0;
                zIndex = 1;
              }
            }

            return (
              <div
                key={index}
                className="absolute inset-0"
                style={{
                  transform,
                  opacity,
                  zIndex,
                  pointerEvents: isActive ? 'none' : 'none',
                  transition: `transform ${TRANSITION_MS}ms cubic-bezier(0.4,0,0.2,1), opacity ${TRANSITION_MS}ms cubic-bezier(0.4,0,0.2,1)`,
                  transformStyle: 'preserve-3d',
                  willChange: 'transform, opacity',
                  borderRadius: !isMobile && isAdj ? 14 : 0,
                  overflow: 'hidden',
                }}
              >
                {/* Parallax + Ken Burns wrapper */}
                <div
                  className="absolute"
                  style={{
                    inset: '-6%', // oversized so parallax shift never shows gaps
                    transform: isActive && !prefersReduced
                      ? `translate(${parallax.x * 0.5}px, ${parallax.y * 0.5}px)`
                      : 'none',
                    transition: 'transform 0.12s ease-out',
                    willChange: 'transform',
                  }}
                >
                  {/* Ken Burns layer */}
                  <div
                    className="absolute inset-0"
                    style={{
                      animation: prefersReduced
                        ? 'none'
                        : `${slide.kenBurns} 11s ease-in-out infinite alternate`,
                      transformOrigin: 'center center',
                    }}
                  >
                    {/* Blur placeholder (tiny file, always visible) */}
                    <img
                      src={slide.blurSrc}
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ filter: 'blur(28px)', transform: 'scale(1.14)' }}
                      alt=""
                      aria-hidden="true"
                      draggable={false}
                    />

                    {/* Full image fades in on load */}
                    <img
                      src={slide.imageSrc}
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{
                        opacity: imagesLoaded[index] ? 1 : 0,
                        transition: 'opacity 1s ease',
                        willChange: 'opacity',
                      }}
                      alt={slide.title}
                      draggable={false}
                      onLoad={() =>
                        setImagesLoaded((prev: boolean[]) => {
                          const n = [...prev];
                          n[index] = true;
                          return n;
                        })
                      }
                    />

                    {/* Video layer — fades in over the static image when available */}
                    {slide.videoSrc && (
                      <video
                        ref={(el: HTMLVideoElement | null) => { videoRefs.current[index] = el; }}
                        src={slide.videoSrc}
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ opacity: 1, zIndex: 1 }}
                        muted
                        loop
                        playsInline
                        preload="metadata"
                      />
                    )}
                  </div>
                </div>

                {/* ── Cinematic overlays ── */}

                {/* Bottom gradient (text legibility) */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      'linear-gradient(to top, rgba(3,8,20,0.95) 0%, rgba(3,8,20,0.6) 28%, rgba(3,8,20,0.15) 55%, transparent 75%)',
                    zIndex: 2,
                  }}
                />
                {/* Top gradient (navbar legibility) */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      'linear-gradient(to bottom, rgba(3,8,20,0.55) 0%, transparent 22%)',
                    zIndex: 2,
                  }}
                />
                {/* Vignette */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      'radial-gradient(ellipse 110% 90% at 50% 50%, transparent 45%, rgba(3,8,20,0.5) 100%)',
                    zIndex: 2,
                  }}
                />
                {/* Light leak (active slide only) */}
                {isActive && !prefersReduced && (
                  <div
                    className="absolute top-0 h-full pointer-events-none"
                    style={{
                      width: '40%',
                      background:
                        'linear-gradient(90deg, transparent, rgba(255,245,210,0.05), transparent)',
                      animation: 'hero-light-leak 9s ease-in-out infinite',
                      animationDelay: '2.5s',
                      zIndex: 3,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* ── Slide text (bottom-left) ── */}
        <div className="absolute bottom-0 left-0 z-30 px-5 sm:px-10 lg:px-16 pb-[4.5rem] sm:pb-16" style={{ maxWidth: 680 }}>
          <div key={textKey}>
            <p
              className="htx-0 text-[10px] sm:text-xs font-black tracking-[0.35em] uppercase mb-3"
              style={{ color: '#E8B400' }}
            >
              {SLIDES[current].label}
            </p>
            <h1
              className="htx-1 text-3xl sm:text-5xl lg:text-[3.5rem] font-black leading-tight text-white mb-4"
            >
              {SLIDES[current].title}
            </h1>
            <p
              className="htx-2 text-slate-300 text-sm sm:text-base max-w-md leading-relaxed mb-7"
            >
              {SLIDES[current].subtitle}
            </p>
            <div className="htx-3 flex flex-wrap gap-3">
              <button
                onClick={() => handleCTA(SLIDES[current].ctaTarget)}
                className="px-6 py-3.5 rounded-xl font-bold text-white text-sm transition-all hover:scale-105 active:scale-95"
                style={{
                  background: 'linear-gradient(135deg,#1B3870,#2a5198)',
                  boxShadow: '0 8px 28px rgba(27,56,112,0.55)',
                }}
              >
                {SLIDES[current].cta} →
              </button>
              <button
                onClick={onApplyClick}
                className="px-6 py-3.5 rounded-xl font-semibold text-sm transition-all hover:bg-white/10 active:scale-95"
                style={{
                  color: 'rgba(255,255,255,0.8)',
                  border: '1px solid rgba(255,255,255,0.22)',
                  backdropFilter: 'blur(6px)',
                }}
              >
                Apply for Admission
              </button>
            </div>
          </div>
        </div>

        {/* ── Left arrow ── */}
        <button
          onClick={prev}
          className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-30 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all hover:scale-110 active:scale-95"
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)',
          }}
          aria-label="Previous slide"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* ── Right arrow ── */}
        <button
          onClick={next}
          className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-30 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all hover:scale-110 active:scale-95"
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)',
          }}
          aria-label="Next slide"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        {/* ── Dot navigation ── */}
        <div className="absolute bottom-5 sm:bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="rounded-full transition-all duration-300"
              style={{
                width: i === current ? 26 : 7,
                height: 7,
                background:
                  i === current ? '#E8B400' : 'rgba(255,255,255,0.35)',
              }}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>

        {/* ── Progress bar ── */}
        <div
          className="absolute bottom-0 left-0 right-0 z-30"
          style={{ height: 2, background: 'rgba(255,255,255,0.08)' }}
        >
          {!isPaused && (
            <div
              key={`pb-${current}-${textKey}`}
              style={{
                height: '100%',
                background: 'linear-gradient(90deg,#1B3870,#3D9B3D,#E8B400)',
                animation: `hero-progress ${ADVANCE_MS}ms linear forwards`,
              }}
            />
          )}
        </div>
      </section>
    </>
  );
};

export default CinematicHeroCarousel;
